import { fetchObjectInfo } from "./save-update-api";

const objectDefinitionRestContextPathCache = new Map();

export async function fetchSchemesBySchemeId(schemeId) {
  try {
    const response = await fetch(
      `/o/c/schemeconfigurators/?filter=id eq '${schemeId}'&restrictFields=actions`,
      {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          // "x-csrf-token": window.Liferay?.authToken || "",
          "Authorization": "Basic " + btoa("prabhudasu:root"),
        },
        credentials: "include",
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch scheme by ID: ${response.status}`);
    }

    const data = await response.json();

    // Return only one item
    return data.items?.[0] || null;
  } catch (error) {
    console.error("Error fetching scheme by ID:", error);
    return null;
  }
}

export function getSchemeTargetObjectDefinitionId(scheme) {
  if (!scheme || typeof scheme !== "object") return null;
  const raw =
    scheme.targetObjectDefinitionId ??
    scheme.targetobjectdefinitionid ??
    scheme.targetObjectId ??
    scheme.properties?.targetObjectDefinitionId ??
    scheme.properties?.targetobjectdefinitionid;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function normalizeObjectDefinitionId(objectDefinitionIdOrScheme) {
  if (
    objectDefinitionIdOrScheme &&
    typeof objectDefinitionIdOrScheme === "object"
  ) {
    return getSchemeTargetObjectDefinitionId(objectDefinitionIdOrScheme);
  }

  const normalizedId = Number(objectDefinitionIdOrScheme);
  return Number.isFinite(normalizedId) && normalizedId > 0
    ? normalizedId
    : null;
}

async function getObjectDefinitionRestContextPath(objectDefinitionIdOrScheme) {
  const resolvedObjectDefinitionId = normalizeObjectDefinitionId(
    objectDefinitionIdOrScheme,
  );

  if (!resolvedObjectDefinitionId) {
    throw new Error("A valid objectDefinitionId is required");
  }

  if (objectDefinitionRestContextPathCache.has(resolvedObjectDefinitionId)) {
    return objectDefinitionRestContextPathCache.get(resolvedObjectDefinitionId);
  }

  const objectDef = await fetchObjectInfo(resolvedObjectDefinitionId);
  const restContextPath = objectDef?.restContextPath || "";

  if (!restContextPath) {
    throw new Error("Object definition is missing restContextPath");
  }

  objectDefinitionRestContextPathCache.set(
    resolvedObjectDefinitionId,
    restContextPath,
  );

  return restContextPath;
}

function normalizeStatusFilterValue(status) {
  if (status === null || status === undefined || status === "") {
    return null;
  }

  if (typeof status === "object") {
    return (
      status.code ?? status.key ?? status.label ?? status.label_i18n ?? null
    );
  }

  return status;
}

function matchesApplicationStatus(statusField, expectedStatus) {
  const normalizedExpectedStatus = normalizeStatusFilterValue(expectedStatus);

  if (normalizedExpectedStatus === null) {
    return true;
  }

  const comparableValues = [
    statusField?.code,
    statusField?.key,
    statusField?.label,
    statusField?.label_i18n,
    statusField,
  ]
    .filter((value) => value !== null && value !== undefined && value !== "")
    .map((value) => String(value).trim().toLowerCase());

  return comparableValues.includes(
    String(normalizedExpectedStatus).trim().toLowerCase(),
  );
}

export async function checkUserAlreadyApplied(
  objectDefinitionId,
  loginUserId,
  status = null,
) {
  try {
    const resolvedObjectDefinitionId =
      normalizeObjectDefinitionId(objectDefinitionId);

    if (!resolvedObjectDefinitionId) {
      throw new Error("A valid objectDefinitionId is required");
    }

    if (!loginUserId) {
      throw new Error("loginUserId is required");
    }

    const baseUrl = await getObjectDefinitionRestContextPath(
      resolvedObjectDefinitionId,
    );

    // Build filter parameter - always filter by loginUserId
    const filters = [`loginuserid eq ${loginUserId}`];

    // Status is represented as a picklist object in the response, so
    // filter it client-side after fetching the user's applications.
    const normalizedStatus = normalizeStatusFilterValue(status);

    const finalUrl = `${baseUrl}?filter=${encodeURIComponent(
      filters.join(" and "),
    )}&pageSize=200&restrictFields=actions`;

    const response = await fetch(finalUrl, {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        // "X-CSRF-Token": window.Liferay?.authToken || "",
        "Authorization": "Basic " + btoa("prabhudasu:root"),
      },
      credentials: "include",
    });

    const data = await response.json();

    if (normalizedStatus === null) {
      return data;
    }

    const filteredItems = (data?.items || []).filter((item) =>
      matchesApplicationStatus(item?.status, normalizedStatus),
    );

    return { ...data, items: filteredItems, totalCount: filteredItems.length };
  } catch (error) {
    console.error("Error in checkUserAlreadyApplied:", error);
    return null;
  }
}

export async function getUserRolesById(userId) {
  try {
    const response = await fetch(
      `/o/headless-admin-user/v1.0/user-accounts/${userId}`,
      {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          // "x-csrf-token": window.Liferay?.authToken || "",
          "Authorization": "Basic " + btoa("prabhudasu:root"),
        },
        credentials: "include",
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch user: ${response.status}`);
    }

    return await response.json();
  } catch (err) {
    console.error("Error fetching user roles:", err);
    return [];
  }
}
